import { useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import debounce from 'lodash-es/debounce';
import snakeCase from 'lodash-es/snakeCase';
import { useTranslation } from 'react-i18next';

import { Box, Flex, LinkBox, LinkOverlay } from '@chakra-ui/react';
import {
  Actions,
  getAuthenticatedNavItems,
  getHelpMenuItems,
  getSettingsMenuItems,
  getUnauthenticatedNavItems,
  headerHeight,
  MobileMenu,
  Navigation,
  ReferrerData,
  Search,
  SettingsMenuItem,
  UdacityHeader,
} from '@udacity/udacity-header-v2';
import auth from '@udacity/ureact-hoth';

import { trackEvent } from '~/features/analytics/track-event';
import { hasSkillAssessmentsAccess } from '~/features/assessments/utils';
import { SEARCH_RESULTS_PATH } from '~/features/catalog/constants';
import { USER_TYPE_COOKIE } from '~/features/cookies/constants';
import { deleteAuthedUserCookies } from '~/features/cookies/utils/delete-authed-user-cookies';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { getUserTypeCookie } from '~/features/cookies/utils/get-user-type-cookie';
import { setClientCookie } from '~/features/cookies/utils/set-client-cookie';
import { useCommunities } from '~/features/dashboard/hooks/use-communities';
import { publicEnv } from '~/features/environment/public';
import { getViewerCountryCode } from '~/features/internationalization/utils/get-viewer-geo-codes';
import { useActiveRouteId } from '~/features/page-header/hooks/use-active-route-id';
import { initSessionReplay } from '~/features/session-replay';
import { useIsConnectLearner } from '~/features/sessions/hooks/use-is-connect-learner';
import { useIsSsoLearner } from '~/features/user/hooks/use-is-sso-learner';
import { useUser } from '~/features/user/hooks/use-user';
import { useUserExperience } from '~/features/user/hooks/use-user-experience';
import { useBaseUrl } from '~/hooks/use-base-url';
import { useCatalogPath } from '~/hooks/use-catalog-path';
import { useClientRender } from '~/hooks/use-client-render';
import { useEnterpriseCompanyData } from '~/hooks/use-enterprise-company-data';
import { useI18n } from '~/hooks/use-i18n';
import { extractCompanySlug } from '~/utils/extract-company-slug';

import { filterSettingsMenuItems } from '../utils/filter-settings-menu-items';

import { SearchResults } from './search-results';

const SelectLanguage = dynamic<{}>(() => import('./select-language').then((mod) => mod.SelectLanguage));

function MastersPromoCard() {
  const { t } = useTranslation();
  return (
    <Flex
      alignItems='center'
      as={LinkBox}
      bgGradient='linear(90deg, #060A12 20.17%, #05163D 100%)'
      border='1px solid #061535'
      borderRadius='4px'
      color='white'
      gap={4}
      h='72px'
      overflow='hidden'
      position='relative'
      px={4}
    >
      <Box flexShrink={0} zIndex={3}>
        <Image alt='' height={52} loading='lazy' src='/images/masters-seal.png' width={52} />
      </Box>
      <Box zIndex={2}>
        <Box as='p' fontSize='1rem' fontWeight='400' lineHeight='1.5' whiteSpace='nowrap'>
          <LinkOverlay href='/masters-artificial-intelligence'>{t('navbar.promoCard.mastersTitle')}</LinkOverlay>
        </Box>
        <Box as='p' color='blue.300' fontSize='0.8125rem' fontWeight='400' lineHeight='1.4' whiteSpace='nowrap'>
          {t('navbar.promoCard.mastersSubtitle')}
        </Box>
      </Box>
      <Image
        alt=''
        height={214}
        src='/images/accreditation/banner-navbar-masters-bg.svg'
        width={214}
        style={{
          left: '-66px',
          pointerEvents: 'none',
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1,
        }}
      />
    </Flex>
  );
}

export function PageHeader({ isSimpleMode, isHidden = false }: { isSimpleMode?: boolean; isHidden?: boolean }) {
  const { t } = useTranslation();
  const router = useRouter();
  const baseUrl = useBaseUrl();
  const { user, userProfileData, userCatalog, userType, jwt, userIsLoading, userProfileIsLoading } = useUser();
  const userHasAllAccess = getActiveSubscriptionCookie();
  const { isConnectLearner } = useIsConnectLearner();
  const { isSsoLearner } = useIsSsoLearner();
  const { userExperience } = useUserExperience();
  const clientRender = useClientRender();
  const { isEnUS } = useI18n();
  const searchResultsPath = useCatalogPath(SEARCH_RESULTS_PATH);
  const companySlug = (router.query.companySlug as string) ?? extractCompanySlug(userCatalog?.catalogUrl);
  const shouldHideSearch = userExperience?.hasFocused || userCatalog?.catalogUrl?.startsWith('https://emc');
  const activeRouteId = useActiveRouteId();

  const { data: enterpriseCompanyData } = useEnterpriseCompanyData(companySlug);
  const enterpriseCompanyLogoUrl = enterpriseCompanyData?.logoUrl;
  const enterpriseCompanyName = enterpriseCompanyData?.name;

  const hasSkillAssessmentAccess: boolean = userExperience ? hasSkillAssessmentsAccess(userExperience) : false;

  useEffect(() => {
    if (router.query.companySlug && getUserTypeCookie() !== 'enterprise') {
      setClientCookie(USER_TYPE_COOKIE, 'enterprise');
    }
  }, [router.query.companySlug]);

  let authenticatedNavItems = getAuthenticatedNavItems({
    baseUrl,
    activeId: activeRouteId,
    isUserSubscribed: Boolean(userHasAllAccess),
    dashboardUrl: '/dashboard',
    enterpriseCatalogUrl: userCatalog?.hasEnterpriseCatalog ? userCatalog.catalogUrl ?? undefined : undefined,
    isConnectLearner,
    hasSkillAssessmentAccess,
    t,
  });

  if (userExperience?.hasFocused) {
    authenticatedNavItems = authenticatedNavItems.filter((item) => item.id === 'sessions');
  }

  const referrerData: ReferrerData | undefined = useMemo(() => {
    return user && userHasAllAccess
      ? {
          programId: '53db30e7-ba1c-4451-920d-13e1c89ba5d9',
          userId: user.id,
          firstName: user.firstName ?? undefined,
          lastName: user.lastName ?? undefined,
          email: user.email,
        }
      : undefined;
  }, [user, userHasAllAccess]);

  const { allCommunities, firstNonVisitedCommunityUrl: communityUrl } = useCommunities();
  const communityFromUrl = communityUrl ? allCommunities.find((c) => c.url === communityUrl) : null;

  const settingsMenuItems = useMemo(() => {
    const defaultSettingsMenuItems = getSettingsMenuItems(t, baseUrl, referrerData, userType, communityUrl);

    return filterSettingsMenuItems({
      items: defaultSettingsMenuItems,
      hasAiAccess: Boolean(userExperience?.hasAiAccess),
      hasFocused: Boolean(userExperience?.hasFocused),
      isEnterpriseWithoutAllAccess: userType === 'enterprise' && !userHasAllAccess,
      isSsoLearner,
    });
  }, [
    baseUrl,
    referrerData,
    t,
    userExperience?.hasAiAccess,
    userExperience?.hasFocused,
    userHasAllAccess,
    userType,
    communityUrl,
    isSsoLearner,
  ]);

  const helpMenuItems = getHelpMenuItems({
    t,
    supportUrl: userExperience?.supportUrl,
  });

  function handleSearchEnter(searchValue: string) {
    activateSessionReplayForSearch();
    router.push(`${searchResultsPath}&searchValue=${searchValue}`);
  }

  const isLoading = userIsLoading || userProfileIsLoading;

  if (isHidden) {
    return null;
  }

  if (!clientRender || isLoading) {
    return (
      <>
        <Box bg='white' position='fixed' top={0} width='100%' zIndex={100}>
          <UdacityHeader
            authBaseUrl={publicEnv.NEXT_PUBLIC_AUTH_URL}
            authCallbackUrl={baseUrl}
            classroomUrl={publicEnv.NEXT_PUBLIC_CONNECT_URL}
            isUserAuthenticated={false}
            logoUrl={jwt ? '/dashboard' : '/'}
          />
        </Box>
        <Box mb={headerHeight} />
      </>
    );
  }

  return (
    <>
      <Box bg='white' position='fixed' top={0} width='100%' zIndex={100}>
        <UdacityHeader
          authBaseUrl={publicEnv.NEXT_PUBLIC_AUTH_URL}
          authCallbackUrl={clientRender ? window.location.href : baseUrl}
          authenticatedNavItems={authenticatedNavItems}
          classroomUrl={publicEnv.NEXT_PUBLIC_CONNECT_URL}
          enterpriseCompanyLogoUrl={enterpriseCompanyLogoUrl}
          enterpriseCompanyName={enterpriseCompanyName}
          helpMenuItems={helpMenuItems}
          isSearchPreviewRendered={isEnUS}
          isSimpleMode={isSimpleMode}
          isUserAuthenticated={Boolean(userProfileData)}
          linkComponent={Link}
          logoUrl={jwt ? '/dashboard' : '/'}
          mobileBreakpoint='xl'
          mobileMenuSlot={<MobileMenu />}
          navigationSlot={<Navigation />}
          promoCardSlot={<MastersPromoCard />}
          searchSlot={shouldHideSearch ? null : <Search searchResultsSlot={<SearchResults />} />}
          settingsMenuItems={settingsMenuItems}
          unauthenticatedNavItems={getUnauthenticatedNavItems(t, baseUrl, activeRouteId)}
          userName={`${user?.firstName} ${user?.lastName}`}
          userProfileImageUrl={userProfileData?.photoUrl}
          actionsSlot={
            <Flex align='center' gap={4}>
              <Actions languageSelectSlot={<SelectLanguage />} />
            </Flex>
          }
          onSearchChange={handleSearchChange}
          onSearchEnter={handleSearchEnter}
          onSearchOpen={activateSessionReplayForSearch}
          onLogInClick={() =>
            trackEvent({
              name: 'Navbar Link Clicked',
              type: 'click',
              label: 'Navbar Link - Header Link Clicked - Log In',
            })
          }
          onNavItemClick={(navItem) => {
            if (navItem.groupTitle) {
              // Note: here the user has clicked on a link item within a group.
              trackEvent({
                type: 'click',
                name: 'Navbar Link Clicked',
                label: navItem.groupTitle,
                action: `navbar_${snakeCase(navItem.groupTitle)}_clicked`,
                value: navItem.label,
              });
            } else {
              trackEvent({
                type: 'click',
                name: 'Navbar Link Clicked',
                label: navItem.label,
                action: 'navbar_clicked',
              });
            }
          }}
          onSettingsMenuItemClick={(settingsMenuItem: SettingsMenuItem) => {
            if (settingsMenuItem.id === 'logout') {
              deleteAuthedUserCookies();

              auth.signOut({
                env: publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME === 'staging' ? 'staging' : 'production',
                returnUrl: baseUrl,
              });
            }
            if (settingsMenuItem.id === 'refer-a-friend') {
              trackEvent({
                name: 'Profile Icon Clicked',
                type: 'click',
                label: 'Profile Icon Clicked - Refer a Friend',
              });
            }
            if (settingsMenuItem.id === 'community-url' && communityUrl) {
              trackEvent({
                name: 'Settings Menu Community Clicked',
                type: 'click',
                label: 'settings_menu_dropdown_community',
                additionalAttributes: {
                  community_url: communityUrl,
                  company_name: communityFromUrl?.title ?? '',
                  ...(user?.id != null && { user_id: user.id }),
                },
              });
            }
          }}
          onSignUpClick={() =>
            trackEvent({
              name: 'Navbar Link Clicked',
              type: 'click',
              label: 'Navbar Link - Header Link Clicked - Sign Up',
            })
          }
        />
      </Box>
      <Box mb={headerHeight} />
    </>
  );
}

const handleSearchChange = debounce(
  (term: string) =>
    trackEvent({
      name: 'Search Bar Clicked',
      type: 'search',
      action: 'key_words_searched',
      value: term,
      label: 'Search',
    }),
  500
);

// Search is global (mounted in the header on every page). Per CONENG-2258,
// Session Replay must only init when the search experience is *activated*
// — i.e. the user opens the search input or submits a query — not on every
// page load. `onSearchOpen` fires the moment the user clicks/taps the search
// trigger, giving the SDK time to start recording before the first keystroke.
// `onSearchEnter` keeps a belt-and-suspenders fallback for direct submit flows.
function activateSessionReplayForSearch() {
  void initSessionReplay({ countryCode: getViewerCountryCode() });
}
